<script setup lang="ts">
import {computed, onMounted, reactive, ref} from "vue";
import {useRouter} from "vue-router";
import useMassCertificationBatches from "@/communication/composables/useMassCertificationBatches";
import dateHelper from "@/core/helpers/dateHelper";
import {
    isSMSBatch,
    MassCertificationBatchTargetableOption,
    MassCertificationNotificationBatchIndex
} from "@/core/interfaces/MassCertification";
import MessageSourceBadge from "@/communication/components/MessageSourceBadge.vue";
import {ChevronRight} from "@lucide/vue";
import BatchRate from "@/communication/components/batch-tables/sms/BatchRate.vue";
import {Select, SelectContent, SelectItem, SelectTrigger, SelectValue} from "@/core/components/ui/select";
import {AcceptableValue} from "reka-ui";
import {massCertificationApi} from "@/container";

const ALL_TARGETABLES = "all";

const router = useRouter();

const params = reactive<{
    page: number,
    limit: number,
    targetable?: {type: string, id: number}
}>({
    page: 1,
    limit: 10
});

const targetables = ref<MassCertificationBatchTargetableOption[]>([]);
const selectedTargetableKey = ref<string>(ALL_TARGETABLES);

const targetableKey = (targetable: MassCertificationBatchTargetableOption) => `${targetable.type}:${targetable.id}`;

const handleTargetableChange = (value: AcceptableValue) => {
    selectedTargetableKey.value = typeof value === "string" ? value : ALL_TARGETABLES;

    const targetable = targetables.value
        .find((option) => targetableKey(option) === selectedTargetableKey.value);

    params.page = 1;
    params.targetable = targetable ? {type: targetable.type, id: targetable.id} : undefined;
};

onMounted(() => {
    massCertificationApi().batchTargetables()
        .then(response => {
            targetables.value = response.data;
        });
});

const {data, isValidating} = useMassCertificationBatches(params);

const handlePageChange = (page: number) => {
    params.page = page;
};

const handleRowClick = (_event: Event, {item}: { item: MassCertificationNotificationBatchIndex }) => {
    router.push({name: 'patient-administration.communication.message.batch', params: {batchId: item.id}});
};

const handleItemPerPageUpdate = (limit: number) => {
    params.page = 1;
    params.limit = limit;
};

const title = (batch: MassCertificationNotificationBatchIndex) => {
    switch (batch.template?.targetable_type) {
        case "laboratory":
            return 'Envoi depuis espace marque';
        case 'program':
            return 'Envoi depuis module';
        case "survey":
            return "Envoi depuis questionnaire";
        default:
            return 'Envoi ciblé (manuel)';
    }
};

/**
 * Compute the read rate for a given batch.
 */
const computeReadRate = (batch: MassCertificationNotificationBatchIndex): number | null => {
    if (isSMSBatch(batch)) {
        return null;
    }

    if (!batch.conversation_messages_count || batch.conversation_messages_count === 0) {
        return 0;
    }

    const read = batch.read_conversation_messages_count ?? 0;

    return Math.round((read / batch.conversation_messages_count) * 100);
};

/**
 * Compute the interaction rate for a given batch.
 */
const computeInteractionRate = (batch: MassCertificationNotificationBatchIndex): number | null => {
    if (isSMSBatch(batch)) {
        return null;
    }

    if (!batch.conversation_messages_count || batch.conversation_messages_count === 0) {
        return 0;
    }

    const interacted = batch.reacted_conversation_messages_count ?? 0;

    return Math.round((interacted / batch.conversation_messages_count) * 100);
};

/**
 * Headers used for the data table.
 */
const headers = computed(() => {
    return [
        {
            title: 'Date',
            key: 'created_at',
            sortable: false
        },
        {
            title: 'Titre',
            key: 'title',
            sortable: false
        },
        {
            title: 'Patients',
            key: 'count',
            sortable: false
        },
        {
            title: '% Lus',
            key: 'read_rate',
            sortable: false
        },
        {
            title: '% Interactions',
            key: 'interaction_rate',
            sortable: false
        },
        {
            title: '',
            key: 'id',
            sortable: false,
            align: 'end' as const,
            width: 48
        }
    ];
});
</script>

<template>
  <div class="flex flex-col gap-2">
    <div class="flex flex-wrap items-center justify-end gap-4">
      <Select :model-value="selectedTargetableKey" @update:model-value="handleTargetableChange">
        <SelectTrigger class="w-[280px] bg-white">
          <SelectValue placeholder="Filtrer par contexte" />
        </SelectTrigger>
        <SelectContent class="max-h-[300px]">
          <SelectItem :value="ALL_TARGETABLES">
            Tous les contextes
          </SelectItem>
          <SelectItem
            v-for="targetable in targetables"
            :key="targetableKey(targetable)"
            :value="targetableKey(targetable)"
          >
            {{ targetable.name }}
          </SelectItem>
        </SelectContent>
      </Select>
    </div>
    <v-data-table-server
      :items-per-page="params.limit"
      :headers="headers"
      :items="data?.data"
      :items-length="data?.meta?.total || 0"
      :loading="isValidating"
      hover
      class="rounded-lg shadow-md border"
      @update:items-per-page="handleItemPerPageUpdate"
      @update:page="handlePageChange"
      @click:row="handleRowClick"
    >
      <template #[`item.created_at`]="{item}">
        <div>
          <p class="font-semibold">
            {{ dateHelper.shortFormat(item.created_at, 'DD MMMM YYYY') }}
          </p>
          <p class="text-muted-foreground text-sm">
            {{ dateHelper.shortFormat(item.created_at, 'HH:mm') }}
          </p>
        </div>
      </template>
      <template #[`item.title`]="{item}">
        <div class="flex flex-col items-start gap-1">
          <p class="font-medium">
            {{ title(item) }}
          </p>
          <MessageSourceBadge
            :type="item.template?.targetable_type ?? null"
            :name="item.template?.targetable?.name"
          />
        </div>
      </template>
      <template #[`item.count`]="{item}">
        <p>{{ item.conversation_messages_count }}</p>
      </template>
      <template #[`item.read_rate`]="{item}">
        <BatchRate :rate="computeReadRate(item)" primary />
      </template>
      <template #[`item.interaction_rate`]="{item}">
        <BatchRate :rate="computeInteractionRate(item)" />
      </template>
      <template #[`item.id`]>
        <ChevronRight class="w-5 h-5 text-muted-foreground" />
      </template>
    </v-data-table-server>
  </div>
</template>
